import { router } from "@inertiajs/react"
import { ArrowLeft, CheckCircle2, Sparkles, Volume2 } from "lucide-react"
import { useEffect, useState } from "react"

import AudioPlayer from "@/components/conversation/audio-player"
import { AudioShimmer } from "@/components/flashcards/audio-shimmer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { useFlashcardChannel } from "@/hooks/use-flashcard-channel"
import { useFlashcardProps } from "@/hooks/use-flashcard-props"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { Flashcard } from "@/types"

interface Props {
  flashcards: Flashcard[]
}

export default function FlashcardsDue(props: any) {
  const { flashcards } = useFlashcardProps<Props>(props)
  return <FlashcardsDueContent flashcards={flashcards} />
}

const QUALITY_LEVELS = [
  {
    value: 1,
    label: "Again",
    description: "Complete blackout",
    color: "bg-red-600 hover:bg-red-700",
  },
  {
    value: 3,
    label: "Hard",
    description: "Correct with serious difficulty",
    color: "bg-orange-500 hover:bg-orange-600",
  },
  {
    value: 4,
    label: "Good",
    description: "Correct with hesitation",
    color: "bg-blue-600 hover:bg-blue-700",
  },
  {
    value: 5,
    label: "Easy",
    description: "Perfect recall",
    color: "bg-green-600 hover:bg-green-700",
  },
] as const

function FlashcardsDueContent({ flashcards: serverFlashcards }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isRevealed, setIsRevealed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [completedCount, setCompletedCount] = useState(0)

  // Track ActionCable updates separately to merge with server data
  const [audioUpdates, setAudioUpdates] = useState<
    Record<string, Partial<Flashcard>>
  >({})

  // Subscribe to FlashcardChannel for real-time audio updates
  useFlashcardChannel((updatedCard) => {
    setAudioUpdates((prev) => ({
      ...prev,
      [updatedCard.uuid]: updatedCard,
    }))
  })

  // Merge server data with ActionCable updates
  const flashcards = serverFlashcards.map((card) => ({
    ...card,
    ...(audioUpdates[card.uuid] || {}),
  }))

  const currentCard = flashcards[currentIndex]
  const totalCards = flashcards.length
  const progress = (completedCount / totalCards) * 100

  useEffect(() => {
    // Reset reveal state when card changes
    setIsRevealed(false)
  }, [currentIndex])

  const handleQualitySubmit = (quality: number) => {
    if (isSubmitting || !currentCard) return

    setIsSubmitting(true)
    router.post(
      `/flashcards/${currentCard.uuid}/practice`,
      { quality },
      {
        preserveScroll: true,
        onSuccess: () => {
          setIsSubmitting(false)
          setCompletedCount((prev) => prev + 1)

          // Move to next card or finish
          if (currentIndex < totalCards - 1) {
            setCurrentIndex((prev) => prev + 1)
          } else {
            // All cards completed
            router.visit("/flashcards", {
              preserveState: false,
            })
          }
        },
        onError: () => {
          setIsSubmitting(false)
        },
      },
    )
  }

  if (flashcards.length === 0) {
    return (
      <AppLayout breadcrumbs={[{ title: "Flashcards", href: "/flashcards" }]}>
        <div className="mx-auto max-w-2xl space-y-6 p-4">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CheckCircle2 className="mb-4 size-12 text-green-500" />
              <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
                All caught up!
              </h3>
              <p className="mb-6 text-center text-sm text-slate-600 dark:text-slate-400">
                No flashcards due for review right now
              </p>
              <Button onClick={() => router.visit("/flashcards")}>
                <ArrowLeft className="mr-2 size-4" />
                Back to Flashcards
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout
      breadcrumbs={[
        { title: "Flashcards", href: "/flashcards" },
        { title: "Practice", href: "/flashcards/due" },
      ]}
    >
      <div className="flex min-h-[calc(100vh-4rem)] flex-col">
        {/* Minimal Header with Progress */}
        <div className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          <div className="mx-auto max-w-4xl space-y-2">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.visit("/flashcards")}
              >
                <ArrowLeft className="mr-2 size-4" />
                Exit
              </Button>
              <div className="text-xs font-medium text-slate-700 dark:text-slate-300">
                {completedCount} / {totalCards}
              </div>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
        </div>

        {/* Flashcard - Full Screen Focus */}
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <Card className="shadow-lg">
              <CardContent className="p-8 sm:p-12 md:p-16">
                {!isRevealed ? (
                  /* Front */
                  <div className="space-y-8 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-1 dark:bg-yellow-900/20">
                      <Sparkles className="size-4 text-yellow-600 dark:text-yellow-500" />
                      <span className="text-xs font-medium tracking-wide text-yellow-700 uppercase dark:text-yellow-500">
                        Question {currentIndex + 1} of {totalCards}
                      </span>
                    </div>
                    <p className="min-h-[4rem] text-3xl leading-tight font-semibold break-words text-slate-900 sm:text-4xl md:text-5xl dark:text-white">
                      {currentCard.frontText}
                    </p>
                    <Button
                      size="lg"
                      onClick={() => setIsRevealed(true)}
                      className="mt-8 px-12 py-6 text-lg"
                    >
                      Show Answer
                    </Button>
                  </div>
                ) : (
                  /* Back - Question + Answer */
                  <div className="space-y-6 text-center">
                    {/* Question (Context) */}
                    <div className="space-y-3">
                      <div className="inline-flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-1 dark:bg-yellow-900/20">
                        <Sparkles className="size-3.5 text-yellow-600 dark:text-yellow-500" />
                        <span className="text-xs font-medium tracking-wide text-yellow-700 uppercase dark:text-yellow-500">
                          Question
                        </span>
                      </div>
                      <p className="text-lg break-words text-slate-600 dark:text-slate-400">
                        {currentCard.frontText}
                      </p>
                    </div>

                    {/* Visual Separator */}
                    <div className="mx-auto h-px w-24 bg-gradient-to-r from-transparent via-slate-300 to-transparent dark:via-slate-600" />

                    {/* Answer (Hero) */}
                    <div className="space-y-6">
                      <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 dark:bg-blue-900/20">
                        <Volume2 className="size-4 text-blue-600 dark:text-blue-500" />
                        <span className="text-xs font-medium tracking-wide text-blue-700 uppercase dark:text-blue-500">
                          Answer
                        </span>
                      </div>

                      {currentCard.backRomanization && (
                        <p className="min-h-[4rem] text-4xl leading-tight font-bold break-words text-slate-900 sm:text-5xl md:text-6xl dark:text-white">
                          {currentCard.backRomanization}
                        </p>
                      )}

                      <p className="text-2xl break-words text-slate-600 dark:text-slate-400">
                        {currentCard.backText}
                      </p>

                      {/* Audio: shimmer while generating, player when ready */}
                      {(currentCard.audioStatus === "pending" ||
                        currentCard.audioStatus === "failed") && (
                        <div className="mx-auto w-full max-w-md">
                          <AudioShimmer
                            status={currentCard.audioStatus}
                            variant="full"
                          />
                        </div>
                      )}
                      {currentCard.audioStatus !== "pending" &&
                        currentCard.audioStatus !== "failed" &&
                        currentCard.backAudioUrl && (
                          <div className="mx-auto w-full max-w-md">
                            <AudioPlayer
                              audioUrl={currentCard.backAudioUrl}
                              variant="dark"
                              className="w-full px-4 py-3"
                            />
                          </div>
                        )}

                      {currentCard.notes && (
                        <div className="mt-6 rounded-lg bg-slate-50 p-4 text-left dark:bg-slate-800">
                          <p className="text-sm break-words text-slate-700 dark:text-slate-300">
                            <span className="font-medium">Notes: </span>
                            {currentCard.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quality Rating - Fixed Bottom */}
        {isRevealed && (
          <div className="border-t border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900">
            <div className="mx-auto max-w-4xl">
              <div className="mb-3 text-center">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  How well did you know this?
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                {QUALITY_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    onClick={() => handleQualitySubmit(level.value)}
                    disabled={isSubmitting}
                    className={cn(
                      "rounded-lg p-3 text-center font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-50 sm:p-4",
                      level.color,
                    )}
                  >
                    <div className="text-base sm:text-lg">{level.label}</div>
                    <div className="mt-1 text-xs opacity-90">
                      {level.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
